import { FC, useEffect, useState, useRef } from "react";
import { Box, CircularProgress, useTheme } from "@mui/material";
import { FormikHelpers } from "formik";
import ReportsFilterBar from "components/common/reports/ReportFiltersBar/ReportsFilterBar";
import ReportsTopbar from "components/common/reports/ReportsTopbar/ReportsTopbar";
import WorkingHoursFilterForm from "components/forms/Reports/WorkingHoursFilterForm";

import { GraphicalWorkingHoursReport } from "@vilocnv/allsetra-core";

// Data
import { useAppDispatch, useAppSelector, useReportCommonStates } from "hooks";
import { selectAccountKeysState } from "app/data/selectors";
import {
  generateReportThunk,
  getAllKeysByAccountThunk,
  resetReports,
  setReportsFilterData,
  setReportsDateValues
} from "app/features";
import {
  convertToNormalTime,
  formattedWorkingHours,
  reportTransformTimeForAPI,
  getStandardizedReportDates
} from "app/data/helpers";
import { WORKING_HOURS_HEADERS } from "app/data/constants";
import { useTranslation } from "react-i18next";
import { CenterTextBox } from "./Reports.styled";
import { restructureWorkingHoursData } from "app/data/helpers/ReportHelpers/graphicalReportHelpers";
import ChartsAccordion from "components/reports/ChartsAccordion/ChartsAccordion";

const DEFAULT_FILTER_VALUES = {
  key: [],
  group: [],
  object: [],
  objectType: []
};

const GraphicalWorkingHours: FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const {
    drawerSelectedAccountId,
    objectTypes,
    objectTypesLoading,
    objectsLoading,
    accountGroups,
    report,
    reportsLoading,
    filterOpen,
    setFilterOpen,
    dateFormat,
    setDateFormat,
    dateValues,
    setDateValues,
    localReport,
    setLocalReport,
    handleObjectsSearch,
    selectedLocalObjects,
    handleOnChangeReportFilterForm,
    preloadedObjects
  } = useReportCommonStates();

  const [filterValues, setFilterValues] = useState<any>(DEFAULT_FILTER_VALUES);
  const [openModal, setOpenModal] = useState(false);
  const [apiPayload, setApiPayload] = useState(null);

  const parentRef = useRef(null);
  const [parentWidth, setParentWidth] = useState(0);
  const [expandedFirstLevel, setExpandedFirstLevel] = useState<string[]>([]);
  const [expandedSecondLevel, setExpandedSecondLevel] = useState<string[]>([]);

  const { t } = useTranslation(["translation", "tableHeadingsTranslation"]);

  const { accountKeys, loading: accountKeysLoading } = useAppSelector(
    selectAccountKeysState
  );

  const formattedData = formattedWorkingHours(localReport, dateFormat);

  const workingHoursFiltersSubmitHandler = async (
    values: any,
    formikHelpers: FormikHelpers<any>
  ) => {
    formikHelpers.setSubmitting(true);

    const endDate = new Date(dateValues.endDate);
    const startDate = new Date(dateValues.startDate);

    const sameDate = startDate.toDateString() === endDate.toDateString();

    if (sameDate) {
      endDate.setHours(23, 59, 59);
      startDate.setHours(0, 0, 0);
    }

    const formattedEndDate = convertToNormalTime(endDate, true);
    const formattedStartDate = convertToNormalTime(startDate);
    setFilterValues(values);

    const payload = {
      ...(apiPayload ?? {}),
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      ...values,
      daysOfWeek:
        values.daysOfWeek.length === 7 ? [] : values.daysOfWeek.map(Number),
      startTime: reportTransformTimeForAPI(values.startTime + ":00", startDate),
      endTime: reportTransformTimeForAPI(values.endTime + ":00", endDate),
      reportType: 3
    };

    dispatch(setReportsFilterData(payload));
    setApiPayload(payload);

    formikHelpers.setSubmitting(false);
    setFilterOpen(false);
  };

  const handleDateChange = (value: any) => {
    const formattedValue = getStandardizedReportDates(value);
    setDateValues(formattedValue);
    dispatch(setReportsDateValues(formattedValue));
  };

  const handleDateRangeSubmit = () => {
    const startDate = convertToNormalTime(dateValues.startDate);
    const endDate = convertToNormalTime(dateValues.endDate, true);

    let updatedFilterValues = { ...filterValues };
    if (filterValues.daysOfWeek) {
      updatedFilterValues = {
        ...filterValues,
        daysOfWeek: filterValues.daysOfWeek.map(Number),
        startTime: reportTransformTimeForAPI(
          filterValues.startTime + ":00",
          dateValues.startDate
        ),
        endTime: reportTransformTimeForAPI(
          filterValues.endTime + ":00",
          dateValues.endDate
        )
      };
    }

    const payload = {
      ...(apiPayload ?? {}),
      startDate,
      endDate,
      ...updatedFilterValues,
      reportType: 3
    };

    setApiPayload(payload);
  };

  const handleLoadReport = () => {
    dispatch(setReportsFilterData(apiPayload));
    dispatch(generateReportThunk(apiPayload));
  };

  const formObjects = [...selectedLocalObjects];

  //@ts-ignore
  const restructuredData = restructureWorkingHoursData(formattedData);

  useEffect(() => {
    const updateParentWidth = () => {
      if (parentRef.current) {
        //@ts-ignore
        const width = parentRef.current.clientWidth;
        setParentWidth(width);
      }
    };

    updateParentWidth();

    window.addEventListener("resize", updateParentWidth);
    handleDateRangeSubmit();
    return () => {
      window.removeEventListener("resize", updateParentWidth);
    };
  }, [report, formattedData]);

  const handleFirstLevelAccordionChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedFirstLevel((prev) =>
        isExpanded ? [...prev, panel] : prev.filter((p) => p !== panel)
      );
    };

  const handleSecondLevelAccordionChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedSecondLevel((prev) =>
        isExpanded ? [...prev, panel] : prev.filter((p) => p !== panel)
      );
    };

  useEffect(() => {
    // dispatch(resetReports());
    handleDateRangeSubmit();
  }, []);

  useEffect(() => {
    setLocalReport(report);
  }, [report]);

  useEffect(() => {
    if (drawerSelectedAccountId !== null) {
      dispatch(getAllKeysByAccountThunk(drawerSelectedAccountId));
    }
  }, [drawerSelectedAccountId]);

  return (
    <Box>
      <ReportsTopbar
        dropdownTitle={t("reports.graphicalWorkingHours")}
        disabled={reportsLoading ? true : false}
      />
      <Box mx={4}>
        <ReportsFilterBar
          setFilterOpen={setFilterOpen}
          dateFormat={dateFormat}
          setDateFormat={setDateFormat}
          handleDateChange={handleDateChange}
          dateValues={dateValues}
          handleDateRangeSubmit={handleDateRangeSubmit}
          dateRangeLoading={reportsLoading}
          exportHeaders={WORKING_HOURS_HEADERS}
          formattedData={formattedData}
          hideExportButton={true}
          disableExportButton={restructuredData?.length > 0 ? false : true}
          reportType={3}
          reportExportTypeValue={3}
          openModal={openModal}
          setOpenModal={setOpenModal}
          exportModalButton={true}
          handleLoadReport={handleLoadReport}
        />
        <WorkingHoursFilterForm
          fetchOnDebounce={handleObjectsSearch}
          open={filterOpen}
          onClose={() => setFilterOpen(false)}
          onSubmit={workingHoursFiltersSubmitHandler}
          groups={accountGroups}
          objectTypes={objectTypes}
          objects={formObjects.length ? formObjects : preloadedObjects}
          keys={accountKeys}
          dataLoading={
            objectsLoading || objectTypesLoading || accountKeysLoading
          }
          theme={theme}
          //@ts-ignore
          onChange={handleOnChangeReportFilterForm}
        />
        {reportsLoading && (
          <CenterTextBox>
            <CircularProgress />
          </CenterTextBox>
        )}
        {!reportsLoading && restructuredData?.length ? (
          <ChartsAccordion
            expandedFirstLevel={expandedFirstLevel}
            expandedSecondLevel={expandedSecondLevel}
            chartID="g-wh-report"
            data={restructuredData}
            handleFirstLevelAccordionChange={handleFirstLevelAccordionChange}
            handleSecondLevelAccordionChange={handleSecondLevelAccordionChange}
            parentRef={parentRef}
            parentWidth={parentWidth}
            renderGraphComponent={(chartItem) => (
              <GraphicalWorkingHoursReport
                width={parentWidth}
                height={240}
                data={chartItem as any}
                barSize={10}
                xAxisLabel={t("reports.hours")}
                yAxisLabel={t("reports.minutes")}
                dataBars={[
                  {
                    barKey: "ignitionHours",
                    barColor: "rgba(218, 133, 204, 1)"
                  },
                  {
                    barKey: "workingHours",
                    barColor: "rgba(95, 159, 255, 1)"
                  },
                  {
                    barKey: "ptoHours",
                    barColor: "rgba(132, 90, 252, 1)"
                  }
                ]}
              />
            )}
          />
        ) : (
          <CenterTextBox>There are no records to display</CenterTextBox>
        )}
      </Box>
    </Box>
  );
};

export default GraphicalWorkingHours;
